import React, { useState } from 'react'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { OfferContent } from '@src/components/OfferContent/OfferContent'
import { onboardingSteloCgmDarkImage, onboardingSteloCgmImage } from '@src/assets/images'
import { AppStackParamList, CommonAuthorizedStackParamList } from '@src/navigation/types'
import { ProductSelection } from '@src/screens/Marketplace/types/types'
import { formatCurrencyAmount } from '@src/screens/Marketplace/utils/utils'
import { useActiveSubscriptions } from '@src/hooks/useActiveSubscriptions'
import { useGoBack } from '@src/utils/navigation'
import { resolveSteloAddonProducts } from '../utils/hooks/useSteloCgmProducts'

const CLOSE_BUTTON_STYLE = {
  width: 40,
  height: 40,
  borderRadius: 20,
  backgroundColor: 'rgba(142, 169, 156, 0.7)',
} as const

const STELO_ADDON_OFFER_DATA = {
  title: "See Your Body's Response in Real Time",
  description: 'See how food, sleep, and stress affect your glucose.',
  benefits: [
    "See what's affecting your glucose",
    'Learn which foods work for you',
    'Spot hidden spikes and energy crashes',
    'Watch your progress in real time',
  ],
  note: 'No commitment required for recurring plans. You can cancel anytime.',
  primaryActionText: 'Checkout',
  secondaryActionText: 'Not Now',
}

type OptionId = 'recurring' | 'one-time'

const getProductPriceLabel = (price?: number) => {
  if (!price) {
    return
  }

  return `$${formatCurrencyAmount(price)}`
}

export const SteloAddonOffer = () => {
  const navigation = useNavigation<
    StackNavigationProp<AppStackParamList & CommonAuthorizedStackParamList>
  >()
  const route = useRoute<RouteProp<AppStackParamList, 'SteloAddonOffer'>>()
  const goBack = useGoBack()
  const activeSubscriptions = useActiveSubscriptions()
  const { steloProducts } = route.params

  const { recurringProduct, oneTimeProduct } = resolveSteloAddonProducts(steloProducts)

  const hasActiveCgmSubscription = activeSubscriptions.some(
    (subscription) => subscription.primaryProduct.includesCgm,
  )

  const [selectedOptionId, setSelectedOptionId] = useState<OptionId>('recurring')
  const effectiveSelectedOptionId = hasActiveCgmSubscription ? 'one-time' : selectedOptionId

  const selectedProduct =
    effectiveSelectedOptionId === 'recurring' ? recurringProduct : oneTimeProduct
  const primaryActionDisabled = !selectedProduct

  const onCheckoutPress = () => {
    if (!selectedProduct) {
      return
    }

    if (effectiveSelectedOptionId === 'recurring') {
      navigation.navigate('SubscriptionCheckout', {
        product: selectedProduct,
      })
      return
    }

    const selections = [{ product: selectedProduct, quantity: 1 }]
    navigation.navigate('AddonCheckout', {
      selections: selections as [ProductSelection],
    })
  }

  const onOptionPress = (optionId: string) => setSelectedOptionId(optionId as OptionId)

  const options = hasActiveCgmSubscription
    ? [
        {
          id: 'one-time',
          title: 'One-Time',
          description: '2 biosensors one time',
          price: getProductPriceLabel(oneTimeProduct?.price),
        },
      ]
    : [
        {
          id: 'recurring',
          title: 'Recurring Monthly',
          description: '2 biosensors every month',
          badge: 'Best value',
          price: getProductPriceLabel(recurringProduct?.price),
          priceSuffix: '/mo',
        },
        {
          id: 'one-time',
          title: 'One-Time',
          description: '2 biosensors one time',
          price: getProductPriceLabel(oneTimeProduct?.price),
        },
      ]

  return (
    <OfferContent
      title={STELO_ADDON_OFFER_DATA.title}
      description={STELO_ADDON_OFFER_DATA.description}
      benefits={STELO_ADDON_OFFER_DATA.benefits}
      imageSource={onboardingSteloCgmImage.imageSource}
      imageSourceDark={onboardingSteloCgmDarkImage.imageSource}
      options={options}
      selectedOptionId={effectiveSelectedOptionId}
      onOptionPress={onOptionPress}
      note={hasActiveCgmSubscription ? undefined : STELO_ADDON_OFFER_DATA.note}
      primaryActionText={STELO_ADDON_OFFER_DATA.primaryActionText}
      secondaryActionText={STELO_ADDON_OFFER_DATA.secondaryActionText}
      onPrimaryActionPress={onCheckoutPress}
      onSecondaryActionPress={goBack}
      onClosePress={goBack}
      closeButtonStyle={CLOSE_BUTTON_STYLE}
      showCloseButton
      primaryActionDisabled={primaryActionDisabled}
    />
  )
}
